import {EasyCardItemType, PaymentsCardCodeType} from '@web/core/constants'
import {getCardCodeName} from '@web/helpers/policies'
import {
    setupLongInterestFreeInstallments,
    getCardEasyPaysFirstItem,
    getCommonInstallmentMinAmount,
    getCommonInstallments,
} from '@web/slices/card/slice'
import makeSlice from '@web/slices/card/sliceMaker'

import {initOrderSheetByAPI} from '../../actions/familyPay'
import {CardEasyPayView} from '../../apis/response/interface/payMethod/cardEasyPay'

const convertCardType = (cardType: string) => cardType as PaymentsCardCodeType

const slice = makeSlice({
    extraReducers: (builder) => {
        builder.addCase(initOrderSheetByAPI.fulfilled, (state, {payload}) => {
            const {payMethod} = payload.data

            // 카드 간편결제
            state.cardEasyPay.items = payMethod.cardEasyPays.map((cardEasyPay) => {
                const companyCode = convertCardType(cardEasyPay.cardCompanyCode)
                return {
                    category: cardEasyPay.cardCategory,
                    companyCode,
                    companyId: cardEasyPay.cardCompanyId,
                    companyName: `${getCardCodeName(companyCode)}카드`,
                    id: cardEasyPay.cardId,
                    imageUrl: cardEasyPay.cardImageUrl,
                    name: cardEasyPay.cardName,
                    ownerType: cardEasyPay.cardOwnerType,
                    productCode: cardEasyPay.cardProductCode,
                    type: cardEasyPay.cardType ?? 'CREDIT',
                    maskedNumber: cardEasyPay.maskedCardNumber,
                    payService: {
                        payable: cardEasyPay.payServiceAvailable,
                        maintenance: cardEasyPay.payServiceMaintenanceSchedule,
                        supportedMerchant: true,
                    },
                    longTermInstallment: cardEasyPay.longTermInstallment,
                    registeredDateTime: cardEasyPay.registeredDateTime,
                    reward: !cardEasyPay.rewardCard
                        ? null
                        : {
                              rewardAccumulateRate: cardEasyPay.rewardAccumulateRate,
                              rewardPointResidualAmount: cardEasyPay.rewardPointResidualAmount,
                              minAmount: 0,
                          },
                    interestFreeBenefitPromotion: {
                        isAvailable: true,
                        minAmount: 0,
                        month: 0,
                    },
                    plcc: cardEasyPay.hyundaiPlccCard
                        ? {
                              type: 'NAVER_HYUNDAI',
                          }
                        : null,
                    issuerCode: cardEasyPay.cardIssuerCode,
                    baseInstallmentInfo: getCommonInstallments(convertCardType(cardEasyPay.cardCompanyCode)),
                }
            })
            state.cardEasyPay.limitedInstallmentMonth = payMethod.billingPayMethods.limitedInstallmentMonth
            state.cardEasyPay.interestFreeInstallments = setupLongInterestFreeInstallments(
                payMethod.billingPayMethods.cardEasyPay?.interestFreeInstallments.map((info) => ({
                    ...info,
                    minAmount: getCommonInstallmentMinAmount(convertCardType(info.cardCompanyCode)),
                })) ?? [],
                payMethod.billingPayMethods.cardEasyPay?.interestInstallments ?? [],
            )

            const defaultCardId = payMethod.defaultPayment?.cardId
            const firstCard = getCardEasyPaysFirstItem<CardEasyPayView>(({payServiceAvailable}) => payServiceAvailable)(
                payMethod.cardEasyPays,
            )

            /** 자동결제 결제수단으로 등록된 card Id가 없으면 카드 목록의 첫번째 카드를 선택된 카드로 초기화  */
            const selectedCardId = defaultCardId ?? firstCard?.cardId

            state.cardEasyPay.selected.id = selectedCardId ?? ''
            state.cardEasyPay.selected.itemType = !selectedCardId ? EasyCardItemType.ADD_CARD : EasyCardItemType.CARD

            state.creditCardBillDiscounts = payMethod.creditCardBillDiscounts.map((billDiscount) => ({
                benefit: billDiscount.benefit,
                cardCompanyCode: billDiscount.corporationCode as PaymentsCardCodeType,
                discountRate: billDiscount.discountRate,
                startDate: billDiscount.startDate,
                endDate: billDiscount.endDate,
                linkUrl: billDiscount.linkUrl,
                additionDescription: billDiscount.orderAdditionDescription,
                detailDescription: billDiscount.orderDetailDescription,
                rank: billDiscount.rank,
                eventType: billDiscount.eventType,
            }))
            state.partnership.containsCreditCard = payMethod.shinhanCardPartnership.containsCreditCard
            state.partnership.containsShinhanPartnershipCard =
                payMethod.shinhanCardPartnership.containsShinhanPartnershipCard

            if (payMethod.billingPayMethods.cardEasyPay) {
                state.cardEasyPay.billing = {
                    pgCp: payMethod.billingPayMethods.cardEasyPay.pgCp,
                    pgId: payMethod.billingPayMethods.cardEasyPay.pgId,
                    minimumAmount: payMethod.billingPayMethods.cardEasyPay.minimumAmount,
                    maximumAmount: payMethod.billingPayMethods.cardEasyPay.maximumAmount,
                }
            }
        })
    },
})

export default slice.reducer
